import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API, IMAGES } from "@/constants";

const reveal = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export default function BrandStory() {
  const [storyImage, setStoryImage] = useState(IMAGES.palms);
  const [storyImageSecondary, setStoryImageSecondary] = useState(IMAGES.spices);

  useEffect(() => {
    axios.get(`${API}/site/content`).then((r) => {
      if (r.data?.story_image) setStoryImage(r.data.story_image);
      if (r.data?.story_image_secondary) setStoryImageSecondary(r.data.story_image_secondary);
    }).catch(() => {});
  }, []);

  return (
    <section id="story" className="py-20 lg:py-32 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto" data-testid="brand-story-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-6">
          <motion.p {...reveal} className="font-mono text-xs uppercase tracking-[0.25em] text-kera-copper mb-6">
            Our Story
          </motion.p>
          <motion.h2
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.1 }}
            className="font-display font-semibold tracking-tight text-kera-ink text-2xl sm:text-3xl lg:text-4xl leading-tight"
          >
            Rooted in Kerala.
            <span className="block text-kera-copper">Crafted for European palates.</span>
          </motion.h2>
          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.2 }}
            className="mt-8 space-y-5 text-base sm:text-lg text-kera-muted leading-relaxed"
          >
            <p>
              KERA takes its name from <span className="font-display font-semibold text-kera-ink">keram</span> — the coconut palm,
              the tree of life that shades every Kerala courtyard. Our kitchens begin where the
              Malabar coast meets the backwaters: coconut pressed cold, spices sun-cured,
              recipes carried by hand across generations.
            </p>
            <p>
              From our first European home in Ireland to a growing family of food hubs,
              we bring that table to yours — unchanged in spirit, uncompromised in craft.
            </p>
          </motion.div>
          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.3 }}
            className="mt-10 flex gap-10"
          >
            {[
              ["04", "European hubs"],
              ["01", "Origin — Kerala"],
              ["100%", "Small-batch craft"],
            ].map(([num, label]) => (
              <div key={label}>
                <div className="font-display text-3xl text-kera-gold">{num}</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted">{label}</div>
              </div>
            ))}
          </motion.div>
        </div>
        <div className="lg:col-span-6 relative">
          <motion.div {...reveal} className="relative">
            <div className="absolute -inset-3 border border-kera-gold/40 rounded-2xl translate-x-4 translate-y-4" />
            <img
              src={storyImage}
              alt="Golden sun over Kerala coconut groves"
              className="relative rounded-2xl w-full h-[420px] lg:h-[520px] object-cover"
              data-testid="brand-story-image"
            />
            <img
              src={storyImageSecondary}
              alt="Kerala spices"
              className="absolute -bottom-8 -left-4 sm:-left-8 w-36 sm:w-48 h-44 sm:h-56 object-cover rounded-xl border-4 border-white shadow-xl"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
